import type { LayerPattern } from '@pn/core/domain/layer';
import { buildVerticesFromFeatureCollection } from '@pn/services/map/mapbox/custom/geojsonToMercatorTriangles';
import type {
  PatternModule,
  UniformLocations,
} from '@pn/services/map/mapbox/custom/patterns';
import { createProgram } from '@pn/services/map/mapbox/custom/shaders/webglShaderUtils';

type PolygonPatternLayerProps<P extends Record<string, unknown>> = {
  dpr?: number;
  pattern: {
    type: LayerPattern;
    props: P;
    module: PatternModule<P>;
  };
};

export type PolygonLikeFC = GeoJSON.FeatureCollection<
  GeoJSON.Polygon | GeoJSON.MultiPolygon | null
>;

export class PolygonPatternCustomLayer<P extends Record<string, unknown> = any>
  implements mapboxgl.CustomLayerInterface
{
  public readonly id: string;
  public readonly type = 'custom' as const;
  public readonly renderingMode = '2d' as const;

  private map!: mapboxgl.Map;
  private gl!: WebGLRenderingContext | WebGL2RenderingContext;

  private program?: WebGLProgram;
  private vbo?: WebGLBuffer;

  private uniformLocations: UniformLocations = {};

  private aPosLoc = -1;
  private uMatrixLoc: WebGLUniformLocation | null = null;

  private vertexCount = 0;
  private props: PolygonPatternLayerProps<P>;
  private pendingVerts?: Float32Array;

  constructor(
    id: string,
    data: PolygonLikeFC,
    props: PolygonPatternLayerProps<P>
  ) {
    this.id = id;
    this.props = props;

    const verts = buildVerticesFromFeatureCollection(data);
    this.vertexCount = verts.length / 3;
    this.pendingVerts = verts;
  }

  onAdd(map: mapboxgl.Map, gl: WebGLRenderingContext | WebGL2RenderingContext) {
    this.map = map;
    this.gl = gl;

    const { module } = this.props.pattern;
    this.program = createProgram(gl, module.vert, module.frag);

    this.aPosLoc = gl.getAttribLocation(this.program, 'a_pos');
    this.uMatrixLoc = gl.getUniformLocation(this.program, 'u_matrix');

    this.uniformLocations = module.init(gl, this.program);

    this.vbo = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      this.pendingVerts ?? new Float32Array(),
      gl.STATIC_DRAW
    );
    this.pendingVerts = undefined;

    map.triggerRepaint();
  }

  onRemove() {
    const gl = this.gl;
    if (!gl) return;

    this.props.pattern.module.destroy?.(gl);

    if (this.vbo) gl.deleteBuffer(this.vbo);
    if (this.program) gl.deleteProgram(this.program);

    this.vbo = undefined;
    this.program = undefined;
  }

  render(gl: WebGLRenderingContext | WebGL2RenderingContext, matrix: number[]) {
    if (!this.program || !this.vbo || this.vertexCount === 0) return;

    gl.useProgram(this.program);

    // common uniforms
    gl.uniformMatrix4fv(this.uMatrixLoc, false, matrix);

    const dpr = this.props.dpr ?? window.devicePixelRatio ?? 1;

    // pattern-specific uniforms
    this.props.pattern.module.setUniforms(
      { map: this.map, gl, matrix, dpr },
      this.uniformLocations,
      this.props.pattern.props
    );

    // attributes
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.enableVertexAttribArray(this.aPosLoc);
    gl.vertexAttribPointer(this.aPosLoc, 3, gl.FLOAT, false, 0, 0);

    gl.enable(gl.BLEND);
    gl.blendEquation(gl.FUNC_ADD);
    gl.blendFuncSeparate(
      gl.SRC_ALPHA,
      gl.ONE_MINUS_SRC_ALPHA,
      gl.ONE,
      gl.ONE_MINUS_SRC_ALPHA
    );

    gl.drawArrays(gl.TRIANGLES, 0, this.vertexCount);
  }

  setPatternProps(next: Partial<P>) {
    this.props = {
      ...this.props,
      pattern: {
        ...this.props.pattern,
        props: { ...this.props.pattern.props, ...next },
      },
    };
    this.map?.triggerRepaint();
  }

  setData(data: PolygonLikeFC) {
    const verts = buildVerticesFromFeatureCollection(data);
    this.vertexCount = verts.length / 3;

    if (this.gl && this.vbo) {
      this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.vbo);
      this.gl.bufferData(this.gl.ARRAY_BUFFER, verts, this.gl.STATIC_DRAW);
      this.map?.triggerRepaint();
    } else {
      this.pendingVerts = verts;
    }
  }
}
